import * as cheerio from 'cheerio';
// eslint-disable-next-line import/no-cycle
import { Property } from '@/types';
// eslint-disable-next-line import/no-cycle
import { getClassByName } from '@/components/template-builder/getters';
// eslint-disable-next-line import/no-cycle
import { getTemplateIframeDocument } from '../utils/helpers';
// eslint-disable-next-line import/no-cycle
import { updateSectionsInState, removeUnusedTranslations } from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { generateTranslations, setTranslationInState } from '@/components/template-builder/utils/translate';
// eslint-disable-next-line import/no-cycle
import { TRANSLATION_ATTRIBUTE, TRANSLATION_MARKUP } from '@/components/template-builder/utils/constants';

interface MenuItemForRender {
  key: string;
  label: string;
  url: string;
}

function createMenuLink(label: string, url: string, existingLink: Element | null): HTMLAnchorElement {
  const anchor = document.createElement('a');
  anchor.setAttribute('href', url || '#');

  if (existingLink) {
    // Preserve class and data-spm attributes (including translation attributes)
    const attrs = existingLink.attributes;
    for (let i = 0; i < attrs.length; i++) {
      const attr = attrs[i];
      if (attr.name.startsWith('data-spm') || attr.name === 'class') {
        anchor.setAttribute(attr.name, attr.value);
      }
    }

    // Preserve/update text translation
    const existingTranslation = existingLink.querySelector(TRANSLATION_MARKUP);
    if (existingTranslation) {
      const cloned = existingTranslation.cloneNode(false) as Element;
      cloned.textContent = label;
      anchor.appendChild(cloned);
      setTranslationInState(anchor, 'markup');
    } else {
      // No existing translation - create one via generateTranslations
      const escapedLabel = label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const { newHtml } = generateTranslations(`<span>${escapedLabel}</span>`, { newElement: true });
      const $ = cheerio.load(newHtml);
      const translatedSpan = $('span');
      if (translatedSpan.length) {
        anchor.innerHTML = translatedSpan.html() || label;
      } else {
        anchor.textContent = label;
      }
    }

    // Update or create href translation in state
    setTranslationInState(anchor, 'attribute', 'href');
  } else {
    // New item - generate translations for both text and href
    const escapedLabel = label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const escapedUrl = (url || '#').replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    const { newHtml } = generateTranslations(`<a href="${escapedUrl}">${escapedLabel}</a>`, { newElement: true });
    const $ = cheerio.load(newHtml);
    const translatedAnchor = $('a');

    if (translatedAnchor.length) {
      anchor.innerHTML = translatedAnchor.html() || label;
      // Copy href translation attribute
      const hrefTranslationKey = translatedAnchor.attr(`${TRANSLATION_ATTRIBUTE}-href`);
      if (hrefTranslationKey) {
        anchor.setAttribute(`${TRANSLATION_ATTRIBUTE}-href`, hrefTranslationKey);
      }
    } else {
      anchor.textContent = label;
    }
  }

  return anchor;
}

function createMenuItemInnerTable(anchor: HTMLAnchorElement): HTMLTableElement {
  const table = document.createElement('table');
  table.setAttribute('cellspacing', '0');
  table.setAttribute('cellpadding', '0');
  const innerTbody = document.createElement('tbody');
  const innerRow = document.createElement('tr');
  const th = document.createElement('th');
  th.appendChild(anchor);
  innerRow.appendChild(th);
  innerTbody.appendChild(innerRow);
  table.appendChild(innerTbody);
  return table;
}

function createHorizontalContent(items: MenuItemForRender[], existingLinks: Element[]): HTMLTableSectionElement {
  const tbody = document.createElement('tbody');
  const row = document.createElement('tr');
  let linkIndex = 0;

  items.forEach((item) => {
    const td = document.createElement('td');
    td.className = 'spm_menu_item';
    const anchor = createMenuLink(item.label, item.url, existingLinks[linkIndex] || null);
    td.appendChild(createMenuItemInnerTable(anchor));
    row.appendChild(td);
    linkIndex += 1;
  });

  tbody.appendChild(row);
  return tbody;
}

function createVerticalContent(items: MenuItemForRender[], existingLinks: Element[]): HTMLTableSectionElement {
  const tbody = document.createElement('tbody');
  let linkIndex = 0;

  items.forEach((item) => {
    const row = document.createElement('tr');
    const td = document.createElement('td');
    td.className = 'spm_menu_item';
    const anchor = createMenuLink(item.label, item.url, existingLinks[linkIndex] || null);
    td.appendChild(createMenuItemInnerTable(anchor));
    row.appendChild(td);
    tbody.appendChild(row);
    linkIndex += 1;
  });

  return tbody;
}

export const setMenuResponsive = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const element = template?.querySelector(selector);
  if (element) {
    if (property.value === 'yes') {
      element.classList.add('spm_responsive_menu');
    } else {
      element.classList.remove('spm_responsive_menu');
    }
  }
};

export const setMenuItems = (selector: string, property: Property) => {
  if (!property.value || !Array.isArray(property.value)) return;

  const template = getTemplateIframeDocument();
  const orientation = getClassByName(selector, 'spm_menu_') || 'horizontal';

  // Get existing links to preserve translations
  const existingLinks = Array.from(template?.querySelectorAll(`${selector} .spm_menu_item a`) || []);

  const items: MenuItemForRender[] = property.value.map((item: any) => ({
    key: item.key,
    label: item.label || '',
    url: item.url || '',
  }));

  const tbody: HTMLElement = template?.querySelector(`${selector} > tbody`) as HTMLElement;
  if (tbody && items.length > 0) {
    const newTbody = orientation === 'vertical'
      ? createVerticalContent(items, existingLinks)
      : createHorizontalContent(items, existingLinks);
    tbody.replaceWith(newTbody);

    updateSectionsInState(false);
    removeUnusedTranslations();
  }
};

export const setMenuOrientation = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const table = template?.querySelector(selector);
  if (!table) return;

  const containers: HTMLElement[] = Array.from(table.querySelectorAll('.spm_menu_item'));

  const items: MenuItemForRender[] = containers.map((container, index) => {
    const link = container.querySelector('a') as HTMLAnchorElement;
    return {
      key: `spm_menu_item_${index}`,
      label: link?.textContent || '',
      url: link?.getAttribute('href') || '',
    };
  });

  const existingLinks = Array.from(table.querySelectorAll('.spm_menu_item a') || []);

  const tbody: HTMLElement | null = template?.querySelector(`${selector} > tbody`);
  if (tbody && property.value) {
    const newTbody = property.value === 'vertical'
      ? createVerticalContent(items, existingLinks)
      : createHorizontalContent(items, existingLinks);
    tbody.replaceWith(newTbody);
  }
};
